<script lang="ts">
import { defineComponent, PropType } from "vue";
import { scaleLinear } from "d3-scale";
import { formatToPrecision, roundToPrecision } from "@/shared/utils/numbers";

export default defineComponent({
  emits: ["change", "update:modelValue"],
  props: {
    modelValue: {
      type: Number,
    },
    min: {
      required: true,
      type: Number,
    },
    max: {
      required: true,
      type: Number,
    },
    unit: {
      type: String,
      default: "",
    },
    initial: Number,
    variant: {
      type: String as PropType<"horizontal" | "vertical">,
      default: "horizontal",
    },
    precision: {
      type: Number,
      default: 0.1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    format: {
      type: Function as PropType<(v: number) => string>,
      default: null,
    },
  },
  data() {
    return {
      value: this.initial || this.modelValue || 0,
      sliding: false,
    };
  },
  watch: {
    initial() {
      this.value = this.initial || 0;
    },
    modelValue() {
      this.value = this.modelValue || 0;
    },
  },
  computed: {
    dimension() {
      return ((this.value - this.min) / (this.max - this.min)) * 100 + "%";
    },
    formattedValue() {
      if (this.format) {
        return this.format(this.value);
      }
      return formatToPrecision(this.value, (this.precision as number) ?? 0.1);
    },
  },
  methods: {
    onMouseDown(e: MouseEvent) {
      this.sliding = true;
      this.updateValue(this.getMouseEventOffset(e));
    },
    onTouchStart(e: TouchEvent) {
      this.sliding = true;
      this.updateValue(this.getTouchEventOffset(e));
    },
    onMouseUp() {
      this.sliding = false;
      this.$emit("change", this.value);
      this.$emit("update:modelValue", this.value);
    },
    onMouseMove(e: MouseEvent) {
      if (this.sliding) {
        this.updateValue(this.getMouseEventOffset(e));
      }
    },
    onTouchMove(e: TouchEvent) {
      if (this.sliding) {
        this.updateValue(this.getTouchEventOffset(e));
      }
    },
    getMouseEventOffset(e: MouseEvent) {
      return this.variant === "horizontal" ? e.offsetX : e.offsetY;
    },
    getTouchEventOffset(e: TouchEvent) {
      return this.variant === "vertical"
        ? e.targetTouches[0].pageY -
            (e.targetTouches[0].target as HTMLElement).getBoundingClientRect().y
        : e.targetTouches[0].pageX -
            (e.targetTouches[0].target as HTMLElement).getBoundingClientRect()
              .x;
    },
    updateValue(offset: number) {
      const ref = this.$refs.container as HTMLDivElement;
      const scale = scaleLinear()
        .domain(
          this.variant === "horizontal"
            ? [0, ref.getBoundingClientRect().width]
            : [ref.getBoundingClientRect().height, 0]
        )
        .range([this.min, this.max])
        .clamp(true);
      this.value = this.round(scale(offset));
    },
    round(value: number): number {
      return roundToPrecision(value, this.precision as number);
    },
  },
});
</script>

<template>
  <div
    class="slider flex items-center"
    :class="[
      variant,
      variant === 'vertical' ? 'flex-col' : 'flex-row-reverse',
      { disabled: disabled && !sliding },
    ]"
  >
    <div
      class="value text-bluenew text-xl mb-2 font-medium border-b-2 border-bluenew pb-0.5 mb-0.5"
      :class="{ 'ml-2 px-2': variant === 'horizontal' }"
      :style="{
        'min-width': variant === 'horizontal' ? '80px' : undefined,
        width: variant === 'vertical' ? '40px' : undefined,
        'text-align': 'center',
      }"
    >
      <span> {{ formattedValue }}{{ unit }} </span>
    </div>
    <div
      class="container"
      ref="container"
      @touchstart.prevent="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onMouseUp"
      @mousedown="onMouseDown"
      @mouseup="onMouseUp"
      @mousemove="onMouseMove"
    >
      <div
        class="filler"
        :style="{ [variant === 'horizontal' ? 'width' : 'height']: dimension }"
      ></div>
    </div>
  </div>
</template>

<style scoped>
.container {
  position: relative;
  overflow: hidden;

  background: linear-gradient(
    216deg,
    rgba(255, 255, 255, 0.35) -31.97%,
    rgba(255, 255, 255, 0.1) 114.27%
  );
  border-radius: 5px;
}

.disabled .container,
.disabled .filler {
  @apply bg-gray-400;
}

.disabled .value {
  @apply text-gray-400;
  @apply border-gray-400;
}

.filler {
  pointer-events: none;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  @apply bg-midblue;
  border-radius: 0 0 5px 5px;
}

.vertical .container {
  width: 40px;
  height: 260px;
}

.horizontal .container,
.horizontal .filler {
  height: 40px;
}

.horizontal .filler {
  border-radius: 5px 0 0 5px;
}
</style>
